'use client';

import { useCallback, useMemo } from 'react';
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  AgentExecutionCountQueryDto,
  AgentExecutionDto,
} from '@terra/shared-contracts-agent-execution';
import { agentExecutionClient } from '@terra/shared-rest-clients-agent-execution';
import type {
  AgentExecutionStatusType,
  ExecutionTypeType,
} from '@terra/shared-schemas-ai';
import type { LifecycleStateFilterDto } from '@terra/shared-dto-types';
import type { AgentExecutionListSort } from '../../../store/tab/agentExecutionsTabScopedFilterStore';
import {
  AGENT_EXECUTIONS_FETCH_ALL_PAGE_SIZE,
  AGENT_EXECUTIONS_PAGE_SIZE,
  buildAgentExecutionsCountQuery,
  buildAgentExecutionsListQuery,
  resolveNextAgentExecutionsPageSkip,
  type AgentExecutionsListQuery,
} from './agent-executions-list.model';

const AGENT_EXECUTIONS_LIST_QUERY_KEY = 'agent-executions-list';

const fetchAgentExecutionsPage = async ({
  targetId,
  query,
  skip,
  limit,
}: {
  targetId: string;
  query: AgentExecutionsListQuery;
  skip: number;
  limit: number;
}) => {
  const response = await agentExecutionClient.findByTarget({
    params: { targetId },
    query: { ...query, skip, limit },
  });

  if (response.status !== 200) {
    throw new Error('Failed to fetch agent executions');
  }

  return response.body;
};

const fetchAgentExecutionStatusCounts = async ({
  targetId,
  query,
}: {
  targetId: string;
  query: Partial<AgentExecutionCountQueryDto>;
}) => {
  const response = await agentExecutionClient.countByStatus({
    params: { targetId },
    query,
  });

  if (response.status !== 200) {
    throw new Error('Failed to fetch agent execution counts');
  }

  return response.body;
};

const fetchAllAgentExecutions = async ({
  targetId,
  query,
}: {
  targetId: string;
  query: AgentExecutionsListQuery;
}): Promise<AgentExecutionDto[]> => {
  const executions: AgentExecutionDto[] = [];

  for (let skip = 0; ; skip += AGENT_EXECUTIONS_FETCH_ALL_PAGE_SIZE) {
    const page = await fetchAgentExecutionsPage({
      targetId,
      query,
      skip,
      limit: AGENT_EXECUTIONS_FETCH_ALL_PAGE_SIZE,
    });

    executions.push(...page.executions);

    if (
      page.executions.length < AGENT_EXECUTIONS_FETCH_ALL_PAGE_SIZE ||
      executions.length >= page.totalCount
    ) {
      return executions;
    }
  }
};

const sumCounts = (counts: Partial<Record<string, number>> | undefined) =>
  counts
    ? Object.values(counts).reduce<number>(
        (total, count) => total + (count ?? 0),
        0,
      )
    : 0;

export function useAgentExecutionsListData({
  targetId,
  searchTerm,
  statusFilter,
  executionType,
  createdSince,
  lifecycleState,
  sort,
}: {
  targetId: string | null;
  searchTerm: string;
  statusFilter: readonly AgentExecutionStatusType[];
  executionType: ExecutionTypeType | null;
  createdSince: Date | null;
  lifecycleState: LifecycleStateFilterDto | undefined;
  sort: AgentExecutionListSort;
}) {
  const queryClient = useQueryClient();
  const isEnabled = Boolean(targetId);
  const filters = useMemo(
    () => ({ searchTerm, executionType, createdSince, lifecycleState }),
    [createdSince, executionType, lifecycleState, searchTerm],
  );
  const listQuery = useMemo(
    () => buildAgentExecutionsListQuery({ ...filters, statusFilter, sort }),
    [filters, sort, statusFilter],
  );
  const countQuery = useMemo(
    () => buildAgentExecutionsCountQuery(filters),
    [filters],
  );
  const query = useInfiniteQuery({
    queryKey: [AGENT_EXECUTIONS_LIST_QUERY_KEY, targetId, listQuery],
    queryFn: ({ pageParam }) =>
      fetchAgentExecutionsPage({
        targetId: targetId ?? '',
        query: listQuery,
        skip: pageParam,
        limit: AGENT_EXECUTIONS_PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: (_lastPage, pages) =>
      resolveNextAgentExecutionsPageSkip({ pages }),
    enabled: isEnabled,
    refetchOnWindowFocus: false,
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === targetId
        ? keepPreviousData(previousData)
        : undefined,
  });
  const statusCountsQuery = useQuery({
    queryKey: [AGENT_EXECUTIONS_LIST_QUERY_KEY, 'counts', targetId, countQuery],
    queryFn: () =>
      fetchAgentExecutionStatusCounts({
        targetId: targetId ?? '',
        query: countQuery,
      }),
    enabled: isEnabled,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
  const unfilteredCountsQuery = useQuery({
    queryKey: [AGENT_EXECUTIONS_LIST_QUERY_KEY, 'unfiltered', targetId],
    queryFn: () =>
      fetchAgentExecutionStatusCounts({
        targetId: targetId ?? '',
        query: { archived: false },
      }),
    enabled: isEnabled,
    refetchOnWindowFocus: false,
  });
  const { fetchNextPage, isPlaceholderData, isFetching } = query;
  const executions = useMemo(
    () => query.data?.pages.flatMap((page) => page.executions) ?? [],
    [query.data],
  );
  const loadMore = useCallback(() => {
    if (isPlaceholderData || isFetching) {
      return;
    }

    void fetchNextPage();
  }, [fetchNextPage, isFetching, isPlaceholderData]);
  const fetchAll = useCallback(
    () =>
      targetId
        ? fetchAllAgentExecutions({ targetId, query: listQuery })
        : Promise.resolve([]),
    [listQuery, targetId],
  );
  const refresh = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: [AGENT_EXECUTIONS_LIST_QUERY_KEY],
      }),
    [queryClient],
  );

  return {
    executions,
    totalCount: query.data?.pages[0]?.totalCount ?? 0,
    unfilteredTotalCount: sumCounts(unfilteredCountsQuery.data),
    statusCounts: statusCountsQuery.data ?? null,
    isInitialLoading: isEnabled && query.isPending,
    isRefreshing: query.isRefetching && !query.isFetchingNextPage,
    isError: query.isError,
    isFetchingMore: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    loadMore,
    fetchAll,
    refresh,
  };
}
